/**
 * Vue shell for outline rename (notes / database / chapters / scenes / acts).
 *
 * ## TEA split (#237)
 *
 * - Pure Model / Msg / update / Cmd → `useRenameEntityFlow.update.ts`
 * - This file → `useTea` + Cmd runner (`focusInput`, `rename`)
 * - `draft` is a writable computed so `v-model` dispatches `DraftChanged`
 *
 * The caller passes the current display name into `start()` rather than
 * this composable deriving it — scenes need index for `sceneDisplayName`,
 * so capturing it once at Start works uniformly for all entity kinds.
 */
import {
  computed,
  nextTick,
  type ComputedRef,
  type Ref,
  type WritableComputedRef,
} from "vue";
import { assertNever } from "../lib/assertNever";
import { isJust, just, matchMaybe, nothing, type Maybe } from "../lib/maybe";
import { useTea } from "../lib/tea";
import {
  initialRenameEntityModel,
  makeRenameEntityUpdate,
  type RenameEntityCmd,
} from "./useRenameEntityFlow.update";

type FocusableInput = {
  focus: () => void;
  select: () => void;
};

function focusInputFromRef(raw: Maybe<FocusableInput>): void {
  matchMaybe(raw, {
    nothing: () => {},
    just: (target) => {
      target.focus();
      target.select();
    },
  });
}

/**
 * @returns Rename bindings: `renaming` / `draft`, plus `start` / `commit` / `cancel`.
 */
export function useRenameEntityFlow<T>(
  inputRef: Ref<Maybe<FocusableInput>>,
  options: {
    onRename: (from: T, to: string) => void | Promise<void>;
    afterCommit: Maybe<() => void>;
    allowEmpty: boolean;
  },
): {
  renaming: ComputedRef<Maybe<T>>;
  draft: WritableComputedRef<string>;
  start: (id: T, currentDisplay: string) => Promise<void>;
  commit: () => void;
  cancel: () => void;
} {
  const update = makeRenameEntityUpdate<T>(options.allowEmpty);

  async function runCmd(cmd: RenameEntityCmd<T>): Promise<void> {
    switch (cmd.kind) {
      case "focusInput": {
        await nextTick();
        // A `ref` bound inside a `v-for` comes back as an array (Vue `ref_for`),
        // even when a sibling `v-if` means only one instance is mounted.
        const raw = inputRef.value as Maybe<FocusableInput | FocusableInput[]>;
        const targetM = matchMaybe(raw, {
          nothing: () => nothing as Maybe<FocusableInput>,
          just: (value) => {
            const one = Array.isArray(value) ? value.at(-1) : value;
            return typeof one === "undefined" ? nothing : just(one);
          },
        });
        focusInputFromRef(targetM);
        return;
      }
      case "rename":
        // Match prior shell: kick off rename and run afterCommit without
        // awaiting the rename promise (callers may be fire-and-forget).
        void options.onRename(cmd.from, cmd.to);
        if (isJust(options.afterCommit)) options.afterCommit.value();
        return;
      default:
        assertNever(cmd);
    }
  }

  const { model, dispatch } = useTea(initialRenameEntityModel<T>(), update, {
    runCmd: just(runCmd),
  });

  const renaming = computed(() => model.value.renaming);
  const draft = computed({
    get: (): string => model.value.draft,
    set: (value: string): void => {
      void dispatch({ type: "DraftChanged", draft: value });
    },
  });

  async function start(id: T, currentDisplay: string): Promise<void> {
    await dispatch({ type: "Start", id, currentDisplay });
  }

  function commit(): void {
    void dispatch({ type: "Commit" });
  }

  function cancel(): void {
    void dispatch({ type: "Cancel" });
  }

  return { renaming, draft, start, commit, cancel };
}
